import type { ApiClient } from '../client';
import type { FairnessReceipt, PublicTicket } from '@game-platform/fairness';

const IFRAME_PREFIX = '/loot_machine/api/game_platform/iframe/v1';
const PUBLIC_PREFIX = '/loot_machine/api/game_platform/fairness';

export interface FairnessKey {
  keyId: string;
  publicKeyPem: string;
}

function isTicket(v: unknown): v is PublicTicket {
  if (!v || typeof v !== 'object') return false;
  const t = v as Partial<PublicTicket>;
  return (
    typeof t.id === 'string' &&
    typeof t.server_seed_hash === 'string' &&
    typeof t.valid_until_ms === 'number' &&
    typeof t.issued_at_ms === 'number' &&
    !!t.odds &&
    typeof t.odds === 'object'
  );
}

/** A provably-fair receipt from any payload that carries one; null when absent or malformed. */
export function asFairnessReceipt(v: unknown): FairnessReceipt | null {
  if (!v || typeof v !== 'object') return null;
  const r = v as Partial<FairnessReceipt>;
  if (!isTicket(r.ticket) || !Array.isArray(r.slots)) return null;
  return r as FairnessReceipt;
}

/**
 * POST /pool_offers/:id/fairness_tickets — the signed commitment for the next
 * purchase of this pack: the hash of a fresh server seed plus the odds of
 * every volatility level. Valid for ~90 s.
 */
export async function issueFairnessTicket(client: ApiClient, poolOfferId: string | number): Promise<PublicTicket> {
  const raw = await client.request<{ result?: { ticket?: unknown } }>(
    `${IFRAME_PREFIX}/pool_offers/${encodeURIComponent(String(poolOfferId))}/fairness_tickets`,
    { method: 'POST' },
  );
  const ticket = raw.result?.ticket;
  if (!isTicket(ticket)) throw new Error('Malformed provably fair ticket');
  return ticket;
}

/** GET /fairness/keys — the public keys Repackz signs tickets with. */
export async function getFairnessKeys(client: ApiClient): Promise<FairnessKey[]> {
  const raw = await client.request<{
    result?: { keys?: Array<{ key_id?: unknown; public_key_pem?: unknown }> };
  }>(`${PUBLIC_PREFIX}/keys`, { method: 'GET' });
  return (raw.result?.keys ?? []).flatMap((k) =>
    typeof k.key_id === 'string' && typeof k.public_key_pem === 'string'
      ? [{ keyId: k.key_id, publicKeyPem: k.public_key_pem }]
      : [],
  );
}

/** GET /fairness/receipts/:ticket_id — a ticket's receipt; anyone holding the id can read it. */
export async function getFairnessReceipt(client: ApiClient, ticketId: string): Promise<FairnessReceipt | null> {
  const raw = await client.request<{ result?: { receipt?: unknown } }>(
    `${PUBLIC_PREFIX}/receipts/${encodeURIComponent(ticketId)}`,
    { method: 'GET' },
  );
  return asFairnessReceipt(raw.result?.receipt);
}
